import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { format, isSameDay, startOfMonth, startOfQuarter, startOfYear, subDays } from 'date-fns';
import { CalendarRange } from 'lucide-react';
import { useListChampionshipsQuery } from 'modules/championship/store/championships-api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import { UDDateRangeInput, UDDateRangeValue } from 'ud-form/components/datetime/component';
import UDFormLabel from 'ud-form/components/label';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';

interface AnalyticsFiltersProps {
  tournamentId: number;
  onFiltersChange: (filters: { championshipId?: number | null; startDate: string; endDate: string }) => void;
}

interface Period {
  start: Date;
  end: Date;
}

type DatePresetId = 'week' | 'month' | 'quarter' | 'year';

interface DatePreset {
  id: DatePresetId;
  label: MessageDescriptor;
  getStart: (today: Date) => Date;
}

const DATE_PRESETS: readonly DatePreset[] = [
  { id: 'week', label: msg`За последнюю неделю`, getStart: (today) => subDays(today, 7) },
  { id: 'month', label: msg`За месяц`, getStart: (today) => startOfMonth(today) },
  { id: 'quarter', label: msg`За квартал`, getStart: (today) => startOfQuarter(today) },
  { id: 'year', label: msg`За год`, getStart: (today) => startOfYear(today) },
];

const AnalyticsFilters = React.memo(({ tournamentId, onFiltersChange }: AnalyticsFiltersProps) => {
  const { t } = useLingui();
  const today = useMemo(() => new Date(), []);
  const allChampionshipsOption = useMemo<SelectOption<number | null>>(() => ({ label: t`Все чемпионаты`, value: null }), [t]);
  const [selectedChampionshipId, setSelectedChampionshipId] = useState<number | null>(null);
  // Применённый период — по нему строятся запросы. Черновик — то, что сейчас в полях: пока выбрано только
  // начало, он неполный и в запрос не уходит, иначе каждый клик по календарю дёргал бы статистику.
  const [period, setPeriod] = useState<Period>(() => ({ start: subDays(new Date(), 7), end: new Date() }));
  const [draft, setDraft] = useState<UDDateRangeValue>(period);
  // Подсвечена нажатая кнопка, а не та, чьи даты совпали: в первый месяц квартала у «месяца» и «квартала»
  // один диапазон (IMGM-1494). Даты, выставленные руками, — свой период, и кнопки гаснут.
  const [presetId, setPresetId] = useState<DatePresetId | null>('week');
  const { start: startDate, end: endDate } = period;

  const { data: championships, isFetching: isChampionshipsFetching } = useListChampionshipsQuery({
    tournamentId,
    limit: 100,
    offset: 0,
    afterDate: format(startDate, 'yyyy-MM-dd'),
    beforeDate: format(endDate, 'yyyy-MM-dd'),
  });

  useEffect(() => {
    if (selectedChampionshipId === null) return;
    if (isChampionshipsFetching) return;
    const stillPresent = championships?.some((c) => c.id === selectedChampionshipId);
    if (!stillPresent) {
      setSelectedChampionshipId(null);
    }
  }, [championships, isChampionshipsFetching, selectedChampionshipId]);

  const championshipOptions = useMemo<SelectOption<number | null>[]>(() => {
    const items: SelectOption<number | null>[] = [allChampionshipsOption];
    championships?.forEach((c) => items.push({ label: c.name, value: c.id }));
    return items;
  }, [allChampionshipsOption, championships]);

  const selectedOption = useMemo(
    () => championshipOptions.find((o) => o.value === selectedChampionshipId) ?? allChampionshipsOption,
    [allChampionshipsOption, championshipOptions, selectedChampionshipId],
  );

  const handleChampionshipChange = useCallback((option: SingleValue<SelectOption<number | null>>) => {
    setSelectedChampionshipId(option?.value ?? null);
  }, []);

  const handleRangeChange = useCallback(
    (next: UDDateRangeValue) => {
      setDraft(next);
      const { start, end } = next;
      if (!start || !end) return;
      if (isSameDay(start, startDate) && isSameDay(end, endDate)) return;

      setPeriod({ start, end });
      setPresetId(null);
      setSelectedChampionshipId(null);
    },
    [startDate, endDate],
  );

  // Календарь конца закрыли, не выбрав дату (Esc, клик мимо): поля возвращаются к применённому периоду,
  // иначе в «По» пусто, а цифры на экране — за прежний период.
  const handleEndCalendarClose = useCallback(() => {
    setDraft((current) => (current.start && current.end ? current : period));
  }, [period]);

  const handlePresetClick = useCallback((preset: DatePreset) => {
    const now = new Date();
    const next = { start: preset.getStart(now), end: now };
    setPeriod(next);
    setDraft(next);
    setPresetId(preset.id);
    setSelectedChampionshipId(null);
  }, []);

  useEffect(() => {
    onFiltersChange({
      championshipId: selectedChampionshipId,
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
    });
  }, [selectedChampionshipId, startDate, endDate, onFiltersChange]);

  return (
    <UDCard radius="panel" padding="lg" className="flex flex-col gap-4">
      <div>
        <UDFormLabel className="mb-2">
          <Trans>Чемпионат</Trans>
        </UDFormLabel>
        <UDSelect options={championshipOptions} value={selectedOption} onChange={handleChampionshipChange} />
      </div>
      <div className="border-lineSubtle border-t pt-4">
        <UDFormLabel className="mb-2">
          <Trans>Период</Trans>
        </UDFormLabel>
        {/* Кнопки и поля дат — в одном ряду: поля и показывают, какой диапазон дала нажатая кнопка. */}
        <div className="flex flex-wrap items-center gap-2">
          <CalendarRange size={16} className="text-inkMuted mr-1" />
          {DATE_PRESETS.map((preset) => (
            <UDButton
              key={preset.id}
              variant={presetId === preset.id ? 'primary' : 'light'}
              slim
              aria-pressed={presetId === preset.id}
              onClick={() => handlePresetClick(preset)}
            >
              {t(preset.label)}
            </UDButton>
          ))}
          <div className="ml-2 w-80 shrink-0">
            <UDDateRangeInput
              startDate={draft.start}
              endDate={draft.end}
              onChange={handleRangeChange}
              maxDate={today}
              restartOnStartSelect
              endProps={{ onCalendarClose: handleEndCalendarClose }}
            />
          </div>
        </div>
      </div>
    </UDCard>
  );
});

export default AnalyticsFilters;
